import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from "@core/guards/auth.guard";
import { ProfileCompleteGuard } from '@core/guards/profile-complete.guard';
import { DrdpLayoutComponent } from "@views/user/drdp-layout/drdp-layout.component";
import { PermissionGuard } from "@core/guards/permission.guard";
import { Permission } from "@core/enums/permissions";
import { ParentProgressComponent } from "@views/user/reports/parent-progress/parent-progress.component";
import { ClassPlanningComponent } from './class-planning/class-planning.component';
import { GroupDetailComponent } from './group-detail/group-detail.component';
import { CohortProgressComponent } from './cohort-progress/cohort-progress.component';
import { ChildProgressComponent } from '@views/user/reports/child-progress/child-progress.component';
import { GroupComponent } from './group/group.component';
import { RatingCompletionComponent } from './rating-completion/rating-completion.component';
import { ReportTrackingComponent } from './report-tracking/report-tracking.component';
import { DistrictOsepComponent } from './district-osep/district-osep.component';
import { StateOsepComponent } from './state-osep/state-osep.component';
import { TeacherSurveyComponent } from './teacher-survey/teacher-survey.component';
import { StateGroupDetailComponent } from './state-group-detail/state-group-detail.component';
import { StateCohortComponent } from './state-cohort/state-cohort.component';
import { OsepExceptionComponent } from './osep-exception/osep-exception.component';
import { ActivityBankComponent } from './activity-bank/activity-bank.component';
import { StateGroupComponent } from './state-group/state-group.component';

const routes: Routes = [
  {
    path: 'reports',
    canActivateChild: [AuthGuard, ProfileCompleteGuard],
    component: DrdpLayoutComponent,
    children: [
      {
        path: 'child-progress',
        canActivate: [PermissionGuard],
        data: {permission: Permission.ChildProgressReport},
        component: ChildProgressComponent,
      },
      {
        path: '2025-child-progress',
        canActivate: [PermissionGuard],
        data: {permission: Permission.ChildProgressReport, is2025: true},
        component: ChildProgressComponent,
      },
      {
        path: 'class-planning',
        canActivate: [PermissionGuard],
        data: {permission: Permission.ClassPlanningReport},
        component: ClassPlanningComponent,
      },
      {
        path: '2025-class-planning',
        canActivate: [PermissionGuard],
        data: {permission: Permission.ClassPlanningReport, is2025: true},
        component: ClassPlanningComponent,
      },
      {
        path: 'parent-progress',
        canActivate: [PermissionGuard],
        data: {permission: Permission.ParentProgressReport},
        component: ParentProgressComponent,
      },
      {
        path: 'group-detail',
        canActivate: [PermissionGuard],
        data: {permission: Permission.GroupDetailsReport},
        component: GroupDetailComponent,
      },
      {
        path: 'state-group-detail',
        canActivate: [PermissionGuard],
        data: {permission: Permission.StateGroupDetailReport},
        component: StateGroupDetailComponent,
      },
      {
        path: '2025-state-group-detail',
        canActivate: [PermissionGuard],
        data: {permission: Permission.StateGroupDetailReport, is2025: true},
        component: StateGroupDetailComponent,
      },
      {
        path: 'cohort-progress',
        canActivate: [PermissionGuard],
        data: {permission: Permission.CohortProgressReport},
        component: CohortProgressComponent,
      },
      {
        path: '2025-cohort-progress',
        canActivate: [PermissionGuard],
        data: {permission: Permission.CohortProgressReport, is2025: true},
        component: CohortProgressComponent,
      },
      {
        path: 'state-cohort',
        canActivate: [PermissionGuard],
        data: {permission: Permission.StateCohortReport},
        component: StateCohortComponent,
      },
      {
        path: '2025-state-cohort',
        canActivate: [PermissionGuard],
        data: {permission: Permission.StateCohortReport, is2025: true},
        component: StateCohortComponent,
      },
      {
        path: 'group',
        canActivate: [PermissionGuard],
        data: {permission: Permission.GroupReport},
        component: GroupComponent,
      },
      {
        path: 'state-group',
        canActivate: [PermissionGuard],
        data: {permission: Permission.StateGroupReport},
        component: StateGroupComponent,
      },
      {
        path: 'rating-completion',
        canActivate: [PermissionGuard],
        data: {permission: Permission.RatingCompletion},
        component: RatingCompletionComponent,
      },
      {
        path: '2025-rating-completion',
        canActivate: [PermissionGuard],
        data: {permission: Permission.RatingCompletion, is2025: true},
        component: RatingCompletionComponent,
      },
      {
        path: 'report-tracking',
        canActivate: [PermissionGuard],
        data: {permission: Permission.AccessToReportTracking},
        component: ReportTrackingComponent,
      },
      {
        path: 'district-osep',
        canActivate: [PermissionGuard],
        data: {permission: Permission.DistrictOSEPReport},
        component: DistrictOsepComponent,
      },
      {
        path: 'state-osep',
        canActivate: [PermissionGuard],
        data: {permission: Permission.StateOSEPReport},
        component: StateOsepComponent,
      },
      {
        path: 'teacher-survey',
        canActivate: [PermissionGuard],
        data: {permission: Permission.TeacherSurveyResults},
        component: TeacherSurveyComponent,
      },
      {
        path: 'osep-exception',
        canActivate: [PermissionGuard],
        data: {permission: Permission.OSEPException},
        component: OsepExceptionComponent,
      },
      {
        path: '2025-parent-progress',
        canActivate: [PermissionGuard],
        data: {permission: Permission.ParentProgressReport, is2025: true},
        component: ParentProgressComponent,
      },
      {
        path: '2025-group-detail',
        canActivate: [PermissionGuard],
        data: {permission: Permission.GroupDetailsReport, is2025: true},
        component: GroupDetailComponent,
      },
      {
        path: '2025-group',
        canActivate: [PermissionGuard],
        data: {permission: Permission.GroupReport, is2025: true},
        component: GroupComponent,
      },
      {
        path: '2025-state-group',
        canActivate: [PermissionGuard],
        data: {permission: Permission.StateGroupReport, is2025: true},
        component: StateGroupComponent,
      },
      {
        path: 'activities-bank',
        canActivate: [PermissionGuard],
        data: {permission: Permission.ActivitiesBankReport},
        component: ActivityBankComponent,
      }
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class ReportsRoutingModule {
}
